import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRoleBasedAccess } from "./use-role-based-access";

export type OrgAdminFeature = "customer_admin" | "device_diagnosis" | "api_configuration";

/**
 * Per-organisation admin page toggles (set in CRM > Edit Organisation).
 * Off by default; superusers always see every page.
 */
export const useOrgAdminFeatures = () => {
  const { userOrganizationId, isSystemAdmin, isOrgAdmin, isLoading: roleLoading } = useRoleBasedAccess();

  const q = useQuery({
    queryKey: ["org-admin-features", userOrganizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("enable_customer_admin, enable_device_diagnosis, enable_api_configuration" as any)
        .eq("id", userOrganizationId!)
        .maybeSingle();
      if (error || !data) return { customer_admin: false, device_diagnosis: false, api_configuration: false };
      const d = data as any;
      return {
        customer_admin: !!d.enable_customer_admin,
        device_diagnosis: !!d.enable_device_diagnosis,
        api_configuration: !!d.enable_api_configuration,
      };
    },
    enabled: !!userOrganizationId && isOrgAdmin,
    staleTime: 60_000,
  });

  // Only organisation admins are restricted; other roles keep their existing page rules.
  const isEnabled = (f: OrgAdminFeature) => !isOrgAdmin || !!q.data?.[f];
  return { isEnabled, isLoading: roleLoading || (!!userOrganizationId && isOrgAdmin && q.isLoading) };
};
